import { createSelector } from 'reselect'
import qs from 'qs'

import { isProd } from 'utils/environment'

import {
    ALLOWED_REGIONS,
    LOCALE_US_EN,
    DEFAULT_LOCALE,
    DEFAULT_LANGUAGE_CODE,
    PROD_HOSTNAMES,
} from 'models/location/constants'

// Get the region from the path (eg ikea.com/us/en/...)
export const getPathRegion = () => {
    const regionFragment = window.location.pathname.substring(1, 6)
    if (ALLOWED_REGIONS.includes(regionFragment)) {
        return regionFragment
    }
    return LOCALE_US_EN
}

export const getIsIFrame = () => window.self !== window.top

// Get the locale given regionFromPath
// eg. given regionFromPath us/en, return en-us
export const getLocaleFromRegion = (regionFromPath) => {
    if (!regionFromPath || !ALLOWED_REGIONS.includes(regionFromPath)) {
        return DEFAULT_LOCALE
    }
    const localeParts = regionFromPath.split('/')
    if (localeParts[1] === DEFAULT_LOCALE) {
        return DEFAULT_LOCALE
    }
    return localeParts.length === 2 ? `${localeParts[1]}-${localeParts[0]}` : DEFAULT_LOCALE
}

export const getLanguageCodeFromRegion = (regionFromPath) => {
    // Given region us/en, return en
    if (!regionFromPath || !ALLOWED_REGIONS.includes(regionFromPath)) {
        return DEFAULT_LANGUAGE_CODE
    }
    const regionParts = regionFromPath.split('/')
    return regionParts[1]
}

// Returns the basename for routing (what subfolder are we serving from)
export const getRoutingBasename = () => {
    const i = window.location.pathname.indexOf('sugarcube')
    if (i === -1) {
        return '/'
    }
    // Pull path with region from the url
    return window.location.pathname.substring(0, i).concat('sugarcube/')
}

// Returns the url we're serving from (externally)
export const getBaseUrl = () => {
    const shouldRedirect = PROD_HOSTNAMES.includes(window.location.hostname)
    const hostname = shouldRedirect ? 'ww8.ikea.com' : window.location.hostname
    return `https://${hostname}/${getPathRegion()}/ideas/sugarcube`
}

export const getApiPrefix = () => {
    // Use the local proxy server for api calls
    if (window.location.hostname === 'localhost') {
        return `/${getPathRegion()}/ideas/sugarcube/api`
    }

    return `${getBaseUrl()}/api`
}

export const getDesignLabUrl = () => {
    if (!isProd) {
        return `//www.cte.ikeadt.com/${getPathRegion()}/home-design/`
    }
    return `//www.ikea.com/${getPathRegion()}/home-design/`
}

export const getDefaultRedirectUrl = () => `${window.location.origin}${getRoutingBasename()}`

export const getSearch = (state) => state.router.location.search

export const getUrlParams = (state) => qs.parse(state.router.location.search, { ignoreQueryPrefix: true })

export const getPathname = (state) => state.router.location.pathname

export const getHash = (state) => state.router.location.hash.replace('#', '')

export const getCurrentPage = createSelector([getPathname], (pathname) => {
    const location = pathname.slice(1) // Remove the first '/' character
    return location || 'default'
})

export const getRouteCompositionIds = createSelector([getHash], (hash) => {
    const parts = hash.split('/')
    const ids = {
        compositionId: parseInt(parts[0], 10) || null,
        sceneId: parseInt(parts[1], 10) || null,
    }
    return ids
})

export const getRouteCollection = createSelector([getUrlParams], (urlParams) => urlParams.collection || null)

export const getDebugFurnitureId = createSelector([getUrlParams], (urlParams) => urlParams.debugFurnitureId || null)

export const getDebugFurnitureModelUrl = createSelector(
    [getUrlParams],
    (urlParams) => urlParams.debugFurnitureModelUrl || null,
)

export const getRegion = createSelector([getUrlParams], (urlParams) => urlParams.region || getPathRegion())

export const getSubCatalog = createSelector([getUrlParams], (urlParams) => urlParams.subCatalog || null)

export const getCanvasWidth = createSelector([getUrlParams], (urlParams) => parseInt(urlParams.canvasWidth, 10) || null)

export const getMaxTextureSize = createSelector(
    [getUrlParams],
    (urlParams) => parseInt(urlParams.maxTextureSize, 10) || null,
)

export const getMemoryTrackingEnv = createSelector([getUrlParams], (urlParams) => urlParams.memoryTrackingEnv)

export const getHideHeader = createSelector([getUrlParams], (urlParams) =>
    Object.prototype.hasOwnProperty.call(urlParams, 'hideHeader'),
)
